import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { downloadFile } from "@lib/helperFile"
import { CartConfigModel } from "@models/cart/cartConfigModel"
import {
  CartConfifUpdateModel,
  CartDetailModel,
  CartUpdateModel,
} from "@models/cart/cartDetailModel"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type { IAssignUnitImportPreviewOutput } from "@models/cart/assignUnitImportPreviewModel"
import http from "@services/httpService"
import dayjs from "dayjs"

const getCartAuditFieldLabels = (): Record<string, string> => ({
  Code: L("CART_DETAIL_CART_CODE"),
  Name: L("CART_DETAIL_CART_NAME"),
  StartBooking: L("CART_DETAIL_START_BOOKING"),
  EndBooking: L("CART_DETAIL_END_BOOKING"),
  MaxBookingPerEmp: L("CART_DETAIL_CART_MAX_BOOKING_PER_UNIT"),
  ConcurrentBooking: L("CART_DETAIL_CONCURRENT_BOOKING"),
  BookingFee: L("CART_DETAIL_BOOKING_FEE"),
  ApplyCommission: L("CART_DETAIL_BOOKING_APPROVAL"),
  ShowCommission: L("CART_DETAIL_DEPOSIT_APPROVAL"),
})

class CartService {
  public async create(body: any) {
    const result = await http.post("api/services/app/Cart/Create", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async update(body: any) {
    const newBody = CartUpdateModel.assign(body)
    const result = await http.put("api/services/app/Cart/Update", newBody)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Cart/GetAll", {
      params,
    })
    const result = res.data.result

    return result
  }

  public async get(id: number) {
    const res = await http.get("api/services/app/Cart/Get", {
      params: { id },
    })
    const result = CartDetailModel.assign(res.data.result)
    return result
  }

  public async getCartConfig(id: number) {
    const res = await http.get("api/services/app/Cart/GetConfiguration", {
      params: { id },
    })
    const result = CartConfigModel.assign(res.data.result)
    return result
  }

  public async getOverview(id: number) {
    const res = await http.get("api/services/app/Cart/GetOverview", {
      params: { id },
    })

    const result = res.data.result

    return result
  }

  public async getOverviewProductInCard(id: number) {
    const res = await http.get("api/services/app/Cart/GetOverviewUnit", {
      params: { id },
    })

    const result = res.data.result

    return result
  }

  public async getAllProductInCard(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Cart/GetUnitInCard", {
      params,
    })
    const result = res.data.result

    return result
  }
  // Sale Policy
  public async getSalesPolicy(params: any): Promise<any> {
    const res = await http.get("api/services/app/Cart/GetSalesPolicy", {
      params,
    })
    const result = res.data.result

    return result
  }
  public async assignSalesPolicy(body: any): Promise<any> {
    const res = await http.post("api/services/app/Cart/AssignSalesPolicy", body)
    const result = res.data.result

    return result
  }

  public async getAlllistBrokerage(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/DismantlingRequests/GetAll", {
      params,
    })
    const result = res.data.result

    return result
  }

  public async updateConfiguration(body: any) {
    const newBody = CartConfifUpdateModel.assign(body)
    const result = await http.put(
      "api/services/app/Cart/UpdateConfiguration",
      newBody
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async deActive(id: number, isActive: boolean) {
    await this.update({ id, isActive })
  }

  public async updateActive(id: number) {
    const result = await http.put("api/services/app/Cart/UpdateActive", { id })
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async getAllAgencyMasterByCart(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Cart/GetAgencyMaster", {
      params,
    })
    const result = res.data.result

    return result
  }
  public async GetListAgencyMaster(params: any) {
    const res = await http.get("api/services/app/Cart/GetListAgencyMaster", {
      params,
    })
    const result = res.data.result

    return result
  }

  public async assignAgencyMaster(body: any) {
    const result = await http.post(
      "api/services/app/Cart/AssignAgencyMaster",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }
  public async assignProducts(body: any) {
    const result = await http.post("api/services/app/Cart/AssignUnit", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }
  public async getAuditLog(id: any) {
    const res = await http.get("api/services/app/Cart/GetAuditLogs", {
      params: { id },
    })
    const labels = getCartAuditFieldLabels()
    return (res.data.result || []).map((row: any) => {
      ; (row.items || []).forEach((item: any) => {
        item.propertyName = labels[item.propertyName] ?? item.propertyName
      })
      return row
    })
  }

  public async exportCart(params: any, payload: any) {
    const response = await http.post("/api/Export/ExportCart", [...payload], {
      params,
      responseType: "blob",
    })
    downloadFile(response.data, `EXPORT-CARTS-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  /**
   * Export giỏ hàng bản mới (Feature-0202): không có bước chọn cột, BE xuất toàn bộ giỏ hàng khớp filter.
   * `level` = 1 (cơ bản) hoặc 2 (chi tiết).
   *
   * Timeout đặt riêng 120s vì instance chung ở `httpService` chỉ 30s — không đủ cho file nhiều nghìn dòng.
   * BE trả 204 khi không có dòng nào khớp filter, khi đó không tải file.
   */
  public async exportCartV1(params: any, level: number) {
    try {
      const response = await http.get("/api/Export/ExportCartV1", {
        params: { ...params, level },
        responseType: "blob",
        timeout: 120000,
      })

      if (response.status === 204) {
        notifyError(L("EXPORT_EXCEL"), L("CART_EXPORT_NO_DATA"))
        return
      }

      const prefix = level === 2 ? "EXPORT-CART-DETAIL" : "EXPORT-CART"
      downloadFile(response.data, `${prefix}-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
    } catch {
      // `responseType: "blob"` làm body lỗi của ABP về dưới dạng Blob, nên interceptor chung ở
      // httpService không đọc được `error.message` và reject với `undefined` — không có toast nào.
      // Vì vậy phải tự báo lỗi ở đây, nếu không người dùng bấm export mà không thấy gì xảy ra.
      notifyError(L("EXPORT_EXCEL"), L("CART_EXPORT_FAILED"))
    }
  }

  public async exportProductInCart(params: any, payload: any) {
    const response = await http.post(
      "/api/Export/exportProductInCart",
      [...payload],
      {
        params,
        responseType: "blob",
      }
    )
    downloadFile(
      response.data,
      `EXPORT-PRODUCT-IN-CARTS-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }

  public async removeUnit(body: any) {
    const result = await http.post("api/services/app/Cart/RemoveUnit", body)
    return result.data.result
  }

  public async RemoveAgencyMaster(params: any) {
    const result = await http.delete(
      "api/services/app/Cart/RemoveAgencyMaster",
      {
        params,
      }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async getListForAdmin(params?): Promise<any> {
    const res = await http.get("api/services/app/Cart/GetListForAdmin", {
      params,
    })
    const { result } = res.data
    return result
  }

  public async getUserByAgencyId(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/AgencyMasters/GetUserByAgencyId",
      {
        params,
      }
    )
    const result = res.data.result

    return result
  }

  // Feature-0204: preview thông tin sản phẩm trước khi AssignUnitImport
  // POST api/Imports/Cart/PreviewAssignUnitImport?cartId={cartId} (multipart/form-data)
  public async previewAssignUnitImport(
    cartId: number,
    file: File
  ): Promise<IAssignUnitImportPreviewOutput> {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post(
      "api/Imports/Cart/PreviewAssignUnitImport",
      formData,
      {
        params: { cartId },
        headers: { "content-type": "multipart/form-data" },
      }
    )
    return res.data.result
  }

  // AssignUnitToCart
  // body: {
  //   "cartId": 0,
  //   "units": [
  //     {
  //       "projectCode": "string",
  //       "fullUnitCode": "string"
  //     }
  //   ]
  // }
  public async AssignUnitToCart(body: any): Promise<any> {
    const res = await http.post("api/services/app/Cart/AssignUnitImport", body)
    return res.data.result
  }

  // UpdateCartProductViewStatus
  // body: {
  //   "cartId": 0,
  //   "unitIds": [],
  //   "viewStatus": '0' | '1' | '2'
  // }
  public async updateCartProductViewStatus(body: any): Promise<any> {
    const res = await http.put("api/services/app/Cart/UpdateCartProductViewStatus", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }
}

export default new CartService()
